"use client";

import { useCallback, useEffect, useRef } from "react";

import { useAuth } from "@/components/AuthProvider";
import { applyAppBadge, clearAppBadge, fetchBadgeCount, isBadgeSupported } from "@/lib/push/badge";

const UNREAD_REFRESH_EVENT = "yb-unread-refresh";
const SUPPORT_UNREAD_REFRESH_EVENT = "yb-support-unread-refresh";

/**
 * Keeps the installed PWA's app icon badge in step with the unread state the
 * navbars already show. Renders nothing.
 *
 * The badge is recomputed from canonical unread sources rather than carried in
 * the push payload: a payload count is a snapshot of the moment it was sent,
 * and two notifications arriving close together would let the older one
 * overwrite the newer with a stale, lower number. Recomputing on arrival and
 * on resume means the number always reflects the account's current state.
 */
export default function PushBadgeSync() {
  const { supabase, user, role, authStatus, hasBusinessAccess } = useAuth();
  const userId = user?.id || null;

  // Monotonic guard: a slow refresh must never overwrite a newer result.
  const sequenceRef = useRef(0);
  const appliedForUserRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isBadgeSupported()) return;
    if (!supabase || !userId || authStatus !== "authenticated") return;

    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;

    let count = 0;
    try {
      count = await fetchBadgeCount({
        supabase,
        userId,
        role,
        includeOrderNotifications: hasBusinessAccess === true,
      });
    } catch {
      return;
    }

    // A newer refresh already landed, or the account changed mid-flight.
    if (sequence !== sequenceRef.current) return;
    if (appliedForUserRef.current !== userId) return;

    await applyAppBadge(count);
  }, [supabase, userId, role, authStatus, hasBusinessAccess]);

  // Sign-out and account switches must never leave the previous account's
  // count on the icon, so the badge clears before anything is recomputed.
  useEffect(() => {
    if (!isBadgeSupported()) return undefined;

    if (authStatus !== "authenticated" || !userId) {
      appliedForUserRef.current = null;
      sequenceRef.current += 1;
      void clearAppBadge();
      return undefined;
    }

    if (appliedForUserRef.current !== userId) {
      appliedForUserRef.current = userId;
      sequenceRef.current += 1;
      // Clear first so a switched-to account never briefly shows the previous
      // account's number while its own count is loading.
      void clearAppBadge().then(() => refresh());
      return undefined;
    }

    void refresh();
    return undefined;
  }, [authStatus, userId, refresh]);

  useEffect(() => {
    if (!isBadgeSupported()) return undefined;

    const handleRefresh = () => void refresh();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const handleWorkerMessage = (event) => {
      // The worker signals arrival; the count itself is recomputed here.
      if (event?.data?.type === "YB_PUSH_RECEIVED") void refresh();
    };

    window.addEventListener(UNREAD_REFRESH_EVENT, handleRefresh);
    window.addEventListener(SUPPORT_UNREAD_REFRESH_EVENT, handleRefresh);
    window.addEventListener("focus", handleRefresh);
    document.addEventListener("visibilitychange", handleVisibility);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", handleWorkerMessage);
    }

    return () => {
      window.removeEventListener(UNREAD_REFRESH_EVENT, handleRefresh);
      window.removeEventListener(SUPPORT_UNREAD_REFRESH_EVENT, handleRefresh);
      window.removeEventListener("focus", handleRefresh);
      document.removeEventListener("visibilitychange", handleVisibility);
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener("message", handleWorkerMessage);
      }
    };
  }, [refresh]);

  return null;
}
